import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2, Camera, Save, FileText, X, Edit2, Barcode } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import BarcodeScanner from '../components/BarcodeScanner';
import { rowKey } from '../lib/rowKey';







export const InstallationForm = ({ customerId, customerName, customerAddress, installationId, onSaved, onCancel }) => {
  const { user } = useContext(AuthContext);
  const sigRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [scanIndex, setScanIndex] = useState(null);
  const [equipmentNames, setEquipmentNames] = useState([]);

  useEffect(() => {
    axios.get(`${API}/equipment-catalog`).then(r => setEquipmentNames(r.data || [])).catch((err) => console.warn('[InstallationForm]', err));
  }, []);

  const [form, setForm] = useState({
    address: customerAddress || '',
    contact_person: '',
    contact_phone: '',
    date: new Date().toISOString().split('T')[0],
    equipment: [{ _key: rowKey(), name: '', serial_number: '', notes: '' }],
    notes: '',
    signature_name: '',
    signature_data: null,
  });

  useEffect(() => {
    if (installationId) {
      axios.get(`${API}/installations/${installationId}`).then(res => {
        const d = res.data;
        setForm({
          address: d.address || '',
          contact_person: d.contact_person || '',
          contact_phone: d.contact_phone || '',
          date: d.date || '',
          equipment: d.equipment?.length ? d.equipment.map((e) => ({ _key: rowKey(), ...e })) : [{ _key: rowKey(), name: '', serial_number: '', notes: '' }],
          notes: d.notes || '',
          signature_name: d.signature_name || '',
          signature_data: d.signature_data || null,
        });
      }).catch(() => toast.error('Kunne ikke laste montering'));
    }
  }, [installationId]);

  const addEquipment = () => setForm(f => ({ ...f, equipment: [...f.equipment, { _key: rowKey(), name: '', serial_number: '', notes: '' }] }));
  const removeEquipment = (i) => setForm(f => ({ ...f, equipment: f.equipment.filter((_, idx) => idx !== i) }));
  const updateEquipment = (i, field, value) => {
    setForm(f => {
      const eq = [...f.equipment];
      eq[i] = { ...eq[i], [field]: value };
      return { ...f, equipment: eq };
    });
  };

  const handleScan = (serial) => {
    if (scanIndex !== null) {
      updateEquipment(scanIndex, 'serial_number', serial);
      setScanIndex(null);
      toast.success(`Serienummer ${serial} registrert`);
    }
  };

  const handleSave = async () => {
    const validEquipment = form.equipment.filter(e => e.name.trim());
    if (!validEquipment.length) { toast.error('Legg til minst ett utstyr'); return; }

    setLoading(true);
    try {
      let sigData = form.signature_data;
      if (sigRef.current && !sigRef.current.isEmpty()) {
        sigData = sigRef.current.toDataURL('image/png');
      }
      const payload = {
        customer_id: customerId,
        address: form.address,
        contact_person: form.contact_person,
        contact_phone: form.contact_phone,
        date: form.date,
        equipment: validEquipment,
        notes: form.notes,
        signature_name: form.signature_name,
        signature_data: sigData,
      };

      if (installationId) {
        await axios.put(`${API}/installations/${installationId}`, payload);
        toast.success('Montering oppdatert');
      } else {
        await axios.post(`${API}/installations`, payload);
        toast.success('Montering lagret');
      }
      onSaved?.();
    } catch {
      toast.error('Kunne ikke lagre montering');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <datalist id="equipment-name-options">
        {equipmentNames.map((n) => <option key={n} value={n} />)}
      </datalist>
      {/* Barcode scanner dialog */}
      <Dialog open={scanIndex !== null} onOpenChange={(open) => !open && setScanIndex(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Skann serienummer</DialogTitle></DialogHeader>
          <BarcodeScanner onScan={handleScan} onClose={() => setScanIndex(null)} />
        </DialogContent>
      </Dialog>

      {/* Info */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Kundeinformasjon</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm font-medium text-slate-900">{customerName}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Adresse</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} data-testid="inst-address" /></div>
            <div className="space-y-1"><Label className="text-xs">Dato</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="inst-date" /></div>
            <div className="space-y-1"><Label className="text-xs">Kontaktperson</Label><Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} data-testid="inst-contact" /></div>
            <div className="space-y-1"><Label className="text-xs">Telefon</Label><Input value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} data-testid="inst-phone" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Equipment */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Utstyr</CardTitle>
            <Button size="sm" variant="outline" onClick={addEquipment} data-testid="inst-add-equipment"><Plus className="h-3.5 w-3.5 mr-1" />Legg til</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {form.equipment.map((eq, i) => (
            <div key={eq._key || eq.id || i} className="p-3 border border-slate-200 rounded-lg space-y-2" data-testid={`inst-equipment-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">Utstyr {i + 1}</span>
                {form.equipment.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => removeEquipment(i)} className="h-6 w-6 p-0 text-red-400" data-testid={`inst-eq-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                )}
              </div>
              <Input value={eq.name} onChange={e => updateEquipment(i, 'name', e.target.value)} placeholder="Utstyrsnavn" data-testid={`inst-eq-name-${i}`} list="equipment-name-options" />
              <div className="flex gap-2">
                <Input value={eq.serial_number} onChange={e => updateEquipment(i, 'serial_number', e.target.value)} placeholder="Serienummer" className="flex-1" data-testid={`inst-eq-serial-${i}`} />
                <Button variant="outline" size="sm" onClick={() => setScanIndex(i)} className="shrink-0" data-testid={`inst-eq-scan-${i}`}>
                  <Camera className="h-4 w-4" />
                </Button>
              </div>
              <Input value={eq.notes} onChange={e => updateEquipment(i, 'notes', e.target.value)} placeholder="Notater (valgfritt)" data-testid={`inst-eq-notes-${i}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Notater</CardTitle></CardHeader>
        <CardContent>
          <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3} placeholder="Generelle notater..." data-testid="inst-notes" />
        </CardContent>
      </Card>

      {/* Signature */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Signatur</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Input value={form.signature_name} onChange={e => setForm({...form, signature_name: e.target.value})} placeholder="Navn på signerer" data-testid="inst-sig-name" />
          {form.signature_data && !sigRef.current ? (
            <div className="space-y-2">
              <img src={form.signature_data} alt="Signatur" className="border rounded-lg h-24 bg-white" />
              <Button variant="outline" size="sm" onClick={() => setForm({...form, signature_data: null})}>Ny signatur</Button>
            </div>
          ) : (
            <div>
              <div className="border rounded-lg bg-white" style={{ touchAction: 'none' }}>
                <SignatureCanvas
                  ref={sigRef}
                  canvasProps={{ className: 'signature-canvas w-full', style: { height: '120px', width: '100%' } }}
                  data-testid="inst-signature-pad"
                />
              </div>
              <Button variant="ghost" size="sm" onClick={() => sigRef.current?.clear()} className="mt-1 text-xs text-slate-400">Tøm signatur</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 mobile-sticky-actions">
        {onCancel && <Button variant="outline" onClick={onCancel} className="flex-1">Avbryt</Button>}
        <Button onClick={handleSave} disabled={loading} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="inst-save-btn">
          <Save className="h-4 w-4 mr-2" />{loading ? 'Lagrer...' : 'Lagre montering'}
        </Button>
      </div>
    </div>
  );
};

export default InstallationForm;
